import { useCallback, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Loader2 } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { cn } from "@/frontend/lib/utils";

/**
 * Rows within the loaded end at which the next chunk is requested, so the
 * fetch starts before the user reaches the last rendered row.
 */
const LOAD_MORE_THRESHOLD_ROWS = 10;

/**
 * DOM-windowed scroll list over an already-loaded row array, wired for infinite
 * loading. Only the visible window plus overscan exists in the DOM, and
 * `onLoadMore` fires from a sentinel's ref callback when the last rendered row
 * comes within {@link LOAD_MORE_THRESHOLD_ROWS} of the loaded end. A failed
 * chunk shows a retry row and re-arms the guard, so the list never dead-ends.
 * Rows are never measured, so every row must be exactly `estimateSize` tall.
 *
 * @param rows Loaded rows, in render order.
 * @param renderRow Renders one row; the returned node is wrapped in the
 *   absolutely-positioned row container.
 * @param estimateSize Row height in px, which every row must match exactly.
 * @param hasNextPage Whether another chunk exists beyond the loaded rows.
 * @param isFetchingNextPage Whether a chunk fetch is in flight.
 * @param onLoadMore Requests the next chunk.
 * @param isError Whether the last chunk fetch failed; shows the retry row.
 * @param onRetry Refetches the failed chunk from the retry row.
 * @param className Extra classes for the scroll container.
 */
export function VirtualList<T>({
  rows,
  renderRow,
  estimateSize,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  isError,
  onRetry,
  className,
}: {
  rows: T[];
  renderRow: (row: T, index: number) => React.ReactNode;
  estimateSize: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  isError?: boolean;
  onRetry?: () => void;
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateSize,
    overscan: 8,
  });

  // Latest-value refs keep the sentinel's ref callback identity stable while
  // it still reads the current props when it fires.
  const loadMoreRef = useRef(onLoadMore);
  loadMoreRef.current = onLoadMore;
  const rowCountRef = useRef(rows.length);
  rowCountRef.current = rows.length;
  const firedForCountRef = useRef(-1);

  /** Fires the chunk fetch when the sentinel mounts, once per loaded row count. */
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    if (firedForCountRef.current === rowCountRef.current) return;
    firedForCountRef.current = rowCountRef.current;
    loadMoreRef.current();
  }, []);

  const items = virtualizer.getVirtualItems();
  const lastRenderedIndex = items.at(-1)?.index ?? -1;
  const nearEnd = rows.length > 0 && lastRenderedIndex >= rows.length - LOAD_MORE_THRESHOLD_ROWS;
  const shouldLoadMore = !isError && hasNextPage && !isFetchingNextPage && nearEnd;

  // Render-path re-arm: a failed chunk resets the once-per-count guard, so
  // clearing the error (retry, invalidation) lets the sentinel fire again.
  if (isError) firedForCountRef.current = -1;

  return (
    <div ref={scrollRef} className={cn("overflow-y-auto [scrollbar-gutter:stable]", className)}>
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {items.map((item) => {
          const row = rows[item.index];
          if (row === undefined) return null;
          return (
            <div
              key={item.key}
              className="absolute inset-x-0 top-0"
              style={{ transform: `translateY(${item.start}px)` }}
            >
              {renderRow(row, item.index)}
            </div>
          );
        })}
      </div>
      {isError ? (
        <div className="flex h-12 items-center justify-center gap-3">
          <span className="text-caption text-muted-foreground">Couldn't load more</span>
          {onRetry && (
            <Button variant="outline" className="h-8" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      ) : isFetchingNextPage ? (
        <div className="flex h-12 items-center justify-center">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      ) : null}
      {shouldLoadMore && <div key={`load-more-${rows.length}`} ref={sentinelRef} aria-hidden />}
    </div>
  );
}
